import styled, { css } from 'styled-components';
import { THEME } from '@/common/styles/theme';

const hiddenScrollbar = css`
	scrollbar-width: none;
	-ms-overflow-style: none;

	&::-webkit-scrollbar {
		display: none;
		width: 0;
		height: 0;
	}
`;

export const ProfileModal = styled.div`
	position: fixed;
	inset: 0;
	z-index: 6000;
	display: grid;
	place-items: center;
	padding: 28px;
	background: rgba(0, 0, 0, 0.48);
	backdrop-filter: blur(30px);

	@media (max-width: ${THEME.mobileBreakpoint}) {
		display: flex;
		flex-direction: column;
		align-items: stretch;
		justify-content: stretch;
		padding: 0;
		height: var(--app-height, 100dvh);
		min-height: var(--app-height, 100dvh);
		max-height: var(--app-height, 100dvh);
		background: rgba(0, 0, 0, 0.96);
		backdrop-filter: none;
	}
`;

export const ProfileModalCard = styled.article`
	position: relative;
	width: min(820px, calc(100vw - 72px));
	height: min(590px, calc(100vh - 72px));
	display: grid;
	grid-template-columns: minmax(0, 1.18fr) minmax(280px, 0.82fr);
	overflow: hidden;
	border: 1px solid rgba(255, 255, 255, 0.28);
	border-radius: 18px;
	background:
		radial-gradient(circle at 82% 100%, rgba(255, 211, 107, 0.12), transparent 34%),
		linear-gradient(135deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.045)),
		rgba(12, 7, 18, 0.5);
	box-shadow:
		inset 0 1px 0 rgba(255, 255, 255, 0.12),
		0 0 36px rgba(176, 38, 255, 0.18),
		0 24px 68px rgba(0, 0, 0, 0.58);
	backdrop-filter: blur(34px) saturate(1.15);

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 100%;
		height: var(--app-height, 100dvh);
		min-height: var(--app-height, 100dvh);
		max-height: var(--app-height, 100dvh);
		display: flex;
		flex-direction: column;
		grid-template-columns: 1fr;
		border: 0;
		border-radius: 0;
		box-shadow: none;
		overflow: hidden;
		backdrop-filter: none;
	}
`;

export const ProfileModalClose = styled.button`
	position: absolute;
	top: 18px;
	right: 18px;
	z-index: 3;
	width: 42px;
	height: 42px;
	border: 0;
	border-radius: 50%;
	background: rgba(0, 0, 0, 0.34);
	color: rgba(255, 255, 255, 0.84);
	font-size: 1.8rem;
	line-height: 1;
	cursor: pointer;
	backdrop-filter: blur(10px);
	transition:
		background 160ms ease,
		color 160ms ease;

	&:hover,
	&:focus-visible {
		color: #ffffff;
		background: rgba(255, 77, 242, 0.2);
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		top: max(14px, env(safe-area-inset-top, 0px));
		right: max(14px, env(safe-area-inset-right, 0px));
	}
`;

export const GalleryVideo = styled.video`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
	object-position: center top;
	pointer-events: none;
	opacity: ${(props) => (props.$visible ? 1 : 0)};
	visibility: ${(props) => (props.$visible ? 'visible' : 'hidden')};
	z-index: ${(props) => (props.$visible ? 2 : 1)};
	transition: opacity 220ms ease, visibility 220ms ease;
`;

export const GallerySkeleton = styled.div`
	position: absolute;
	inset: 0;
	z-index: 0;
`;

export const ProfileModalImage = styled.img`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
	object-position: center top;
	display: block;
	opacity: ${(props) => (props.$visible ? 1 : 0)};
	visibility: ${(props) => (props.$visible ? 'visible' : 'hidden')};
	transition: opacity 220ms ease, visibility 220ms ease;
	z-index: 1;
`;

export const ProfileModalGallery = styled.section`
	position: relative;
	height: 100%;
	min-height: 0;
	background: #050208;
	overflow: hidden;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		flex: 0 0 auto;
		width: 100%;
		height: auto;
		min-height: 0;
		max-height: min(58dvh, calc(var(--app-height, 100dvh) * 0.58));
		aspect-ratio: 0.72;
	}
`;

export const ProfileModalNav = styled.button`
	position: absolute;
	top: 50%;
	z-index: 4;
	width: 44px;
	height: 44px;
	border: 1px solid rgba(255, 255, 255, 0.28);
	border-radius: 50%;
	color: #fff;
	font-size: 2rem;
	line-height: 1;
	background: rgba(0, 0, 0, 0.28);
	backdrop-filter: blur(12px);
	transform: translateY(-50%);
	cursor: pointer;
	display: grid;
	place-items: center;
	padding: 0;

	&:disabled {
		opacity: 0.35;
		cursor: not-allowed;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 38px;
		height: 38px;
	}
`;

export const ProfileModalPrev = styled(ProfileModalNav)`
	left: 18px;
`;

export const ProfileModalNext = styled(ProfileModalNav)`
	right: 18px;
`;

export const ProfileModalThumbs = styled.div`
	position: absolute;
	left: 24px;
	right: 170px;
	bottom: 20px;
	z-index: 4;
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 7px;
	overflow-x: auto;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		left: max(18px, env(safe-area-inset-left, 0px));
		right: max(110px, calc(env(safe-area-inset-right, 0px) + 110px));
		bottom: max(18px, env(safe-area-inset-bottom, 0px));
	}
`;

export const ProfileModalDot = styled.button`
	flex: 0 0 auto;
	width: 7px;
	height: 7px;
	border: 0;
	border-radius: 50%;
	padding: 0;
	background: rgba(255, 255, 255, 0.38);
	opacity: 0.86;
	box-shadow: 0 0 8px rgba(0, 0, 0, 0.34);
	cursor: pointer;
	transition:
		width 180ms ease,
		background 180ms ease,
		opacity 180ms ease;

	${(props) =>
		props.$active &&
		css`
			width: 18px;
			border-radius: 999px;
			opacity: 1;
			background: #ffffff;
			box-shadow: 0 0 14px rgba(255, 77, 242, 0.42);
		`}
`;

export const ProfileModalDetails = styled.section`
	min-height: 0;
	padding: 30px 30px 30px;
	overflow: auto;
	overscroll-behavior: contain;
	-webkit-overflow-scrolling: touch;
	${hiddenScrollbar}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		flex: 1 1 auto;
		min-height: 0;
		padding:
			20px ${THEME.mobileGutter}
			max(24px, calc(env(safe-area-inset-bottom, 0px) + 12px));
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
		overscroll-behavior: contain;
		${hiddenScrollbar}
	}
`;

export const ProfileModalName = styled.h2`
	margin: 0 48px 16px 0;
	font-family: ${THEME.fontDisplay};
	font-size: clamp(1.85rem, 3vw, 2.65rem);
	line-height: 0.95;
	color: ${THEME.text};
`;

export const ProfileModalMeta = styled.p`
	margin: 0 48px 10px 0;
	color: rgba(255, 255, 255, 0.64);
	font-size: 0.98rem;
`;

export const ProfileModalBio = styled.p`
	margin: 0;
	color: rgba(255, 255, 255, 0.78);
	font-size: 0.92rem;
	line-height: 1.5;
`;

export const ProfileAttributes = styled.div`
	display: grid;
	gap: 20px;
	margin-top: 26px;
	padding-top: 22px;
	border-top: 1px solid rgba(255, 255, 255, 0.12);
`;

export const ProfileAttributeSection = styled.section`
	display: grid;
	gap: 13px;

	h3 {
		margin: 0;
		color: rgba(255, 255, 255, 0.94);
		font-family: ${THEME.fontDisplay};
		font-size: 1.08rem;
		line-height: 1;
	}
`;

export const ProfileAttributeGrid = styled.div`
	display: grid;
	gap: 12px;
`;

export const ProfileAttributeItem = styled.div`
	display: grid;
	grid-template-columns: 28px minmax(0, 1fr);
	gap: 12px;
	align-items: start;
`;

export const ProfileAttributeIcon = styled.span`
	width: 28px;
	height: 28px;
	display: grid;
	place-items: center;
	border-radius: 10px;
	color: rgba(255, 255, 255, 0.52);
	background: rgba(255, 255, 255, 0.035);

	svg {
		width: 19px;
		height: 19px;
		fill: none;
		stroke: currentColor;
		stroke-width: 1.8;
		stroke-linecap: round;
		stroke-linejoin: round;
	}
`;

export const ProfileAttributeCopy = styled.span`
	min-width: 0;
	display: block;

	strong {
		display: block;
		color: rgba(255, 255, 255, 0.94);
		font-size: 0.91rem;
		line-height: 1.12;
	}

	span {
		display: block;
		margin-top: 4px;
		color: rgba(255, 255, 255, 0.68);
		font-size: 0.9rem;
		line-height: 1.18;
	}
`;

export const ProfileModalChat = styled.button`
	position: absolute;
	right: 22px;
	bottom: 26px;
	z-index: 3;
	min-height: 42px;
	border: 1px solid rgba(255, 255, 255, 0.28);
	border-radius: 999px;
	padding: 0 18px;
	color: #fff;
	background:
		linear-gradient(135deg, rgba(255, 255, 255, 0.18), rgba(255, 255, 255, 0.055)),
		rgba(10, 4, 14, 0.34);
	box-shadow:
		0 16px 34px rgba(0, 0, 0, 0.28),
		inset 0 1px 0 rgba(255, 255, 255, 0.16);
	backdrop-filter: blur(16px) saturate(1.18);
	font: inherit;
	font-size: 0.88rem;
	font-weight: 900;
	cursor: pointer;
	transition:
		transform 180ms ease,
		border-color 180ms ease,
		background 180ms ease,
		box-shadow 180ms ease;

	&:hover,
	&:focus-visible {
		transform: translateY(-2px);
		border-color: rgba(255, 126, 237, 0.52);
		background:
			linear-gradient(135deg, rgba(255, 77, 242, 0.24), rgba(255, 211, 107, 0.14)),
			rgba(10, 4, 14, 0.42);
		box-shadow:
			0 18px 38px rgba(0, 0, 0, 0.32),
			0 0 22px rgba(255, 77, 242, 0.14),
			inset 0 1px 0 rgba(255, 255, 255, 0.18);
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		right: max(18px, env(safe-area-inset-right, 0px));
		bottom: max(18px, env(safe-area-inset-bottom, 0px));
		min-height: 40px;
		padding: 0 15px;
		font-size: 0.82rem;
	}
`;
